import React, { useCallback, useEffect, useState } from "react";
import { api, API, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { EnvelopeSimple, WhatsappLogo, QrCode, X } from "@phosphor-icons/react";
import { toast } from "sonner";

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

// ============ Send Dialog (Email + WhatsApp) ============
export function SendInvoiceDialog({ invoice, company }) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("email");
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    if (!invoice?.id) return;
    api.get(`/deliveries`, { params: { parent_id: invoice.id } }).then((r) => setHistory(r.data));
  }, [invoice?.id]);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  const send = async () => {
    if (!recipient) return toast.error("Recipient required");
    setBusy(true);
    try {
      const path = channel === "email" ? "send-email" : "send-whatsapp";
      await api.post(`/invoices/${invoice.id}/${path}`, {
        recipient, channel, subject, message,
      });
      toast.success(`${channel === "email" ? "Email" : "WhatsApp"} sent`);
      setRecipient(""); setSubject(""); setMessage("");
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(detail);
    } finally {
      setBusy(false);
      loadHistory();  // backend logs a delivery record on both success and failure
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`send-invoice-${invoice.id}`}
          title="Send"
          className="text-slate-500 hover:text-[#EA580C] p-1"
        >
          <EnvelopeSimple size={14} />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="send-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Send Invoice {invoice.invoice_number}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setChannel("email")}
            data-testid="send-channel-email"
            className={`flex-1 flex items-center justify-center gap-2 py-2 border rounded-sm text-sm font-semibold ${channel === "email" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-300 text-slate-700"}`}
          >
            <EnvelopeSimple size={16} /> Email
          </button>
          <button
            onClick={() => setChannel("whatsapp")}
            data-testid="send-channel-whatsapp"
            className={`flex-1 flex items-center justify-center gap-2 py-2 border rounded-sm text-sm font-semibold ${channel === "whatsapp" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-300 text-slate-700"}`}
          >
            <WhatsappLogo size={16} weight="fill" /> WhatsApp
          </button>
        </div>

        <div className="space-y-3">
          <Field label={channel === "email" ? "Recipient email" : "WhatsApp number (with +country)"}>
            <Input
              data-testid="send-recipient-input"
              placeholder={channel === "email" ? "client@example.com" : "+91XXXXXXXXXX"}
              value={recipient} onChange={(e) => setRecipient(e.target.value)}
            />
          </Field>
          {channel === "email" && (
            <Field label="Subject (optional)">
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder={`Invoice ${invoice.invoice_number} from ${company?.name || "Celsius"}`} />
            </Field>
          )}
          <Field label="Message (optional)">
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Leave blank to use a professional default message." />
          </Field>
          <Button
            onClick={send} disabled={busy}
            data-testid="send-invoice-submit-button"
            className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
          >
            {busy ? "Sending..." : `Send via ${channel === "email" ? "Email" : "WhatsApp"}`}
          </Button>
        </div>

        {history.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="label-caps mb-2">Delivery History</div>
            <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="delivery-history">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 px-2 py-1.5 rounded-sm">
                  <div className="flex items-center gap-2">
                    {h.channel === "email" ? <EnvelopeSimple size={12} /> : <WhatsappLogo size={12} weight="fill" />}
                    <span className="font-medium truncate max-w-[180px]">{h.recipient}</span>
                  </div>
                  <span className={`uppercase tracking-widest font-semibold ${
                    h.status === "sent" || h.status === "delivered" ? "text-emerald-700" :
                    h.status === "failed" || h.status === "not_configured" ? "text-red-700" :
                    "text-slate-500"
                  }`}>
                    {h.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ E-Invoice IRN/QR Dialog ============
export function EInvoiceDialog({ invoice, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [providerInfo, setProviderInfo] = useState(null);
  const einv = invoice.einvoice;

  useEffect(() => {
    if (open) api.get("/einvoice/provider").then((r) => setProviderInfo(r.data)).catch(() => {});
   
  }, [open]);

  useEffect(() => {
    let revoke = null;
    (async () => {
      if (!open || !einv) return;
      const res = await fetch(`${API}/invoices/${invoice.id}/einvoice/qr.png`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setQrUrl(url); revoke = url;
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [open, invoice.id, einv]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/einvoice`);
      toast.success(providerInfo?.mode === "SANDBOX" ? "e-Invoice IRN generated (SANDBOX)" : "e-Invoice IRN generated");
      onGenerated && onGenerated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const mode = providerInfo?.mode || "SANDBOX";
  const provider = providerInfo?.provider || "sandbox";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`einvoice-${invoice.id}`}
          title="e-Invoice IRN & QR"
          className={`p-1 ${einv?.irn ? "text-[#EA580C]" : "text-slate-500 hover:text-[#EA580C]"}`}
        >
          <QrCode size={14} weight={einv?.irn ? "fill" : "regular"} />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="einvoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">e-Invoice — {invoice.invoice_number}</DialogTitle>
        </DialogHeader>

        {!einv ? (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              Generate a compliant IRN + QR for this invoice.
              {mode === "SANDBOX" ? (
                <span className="block mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 rounded-sm" data-testid="einvoice-current-mode">
                  SANDBOX MODE (provider: {provider}) — Production requires GSP or IRP-direct credentials.
                </span>
              ) : (
                <span className="block mt-2 text-xs bg-sky-50 border border-sky-200 text-sky-800 px-2 py-1 rounded-sm" data-testid="einvoice-current-mode">
                  {mode} — provider: {provider}
                </span>
              )}
            </div>
            <Button onClick={generate} disabled={busy} data-testid="generate-einvoice-button" className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
              {busy ? "Generating…" : "Generate IRN + QR"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-1 rounded-sm inline-block" data-testid="einvoice-mode-badge">
              {einv.mode}
            </div>
            <div>
              <div className="label-caps">IRN</div>
              <div className="font-mono text-[11px] break-all mt-1" data-testid="einvoice-irn">{einv.irn}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><div className="label-caps">Ack No.</div><div className="font-mono-num text-sm mt-1">{einv.ack_no}</div></div>
              <div><div className="label-caps">FY</div><div className="text-sm mt-1">{einv.financial_year}</div></div>
            </div>
            {qrUrl && (
              <div className="flex justify-center bg-white border border-slate-200 p-4 rounded-sm">
                <img src={qrUrl} alt="e-Invoice QR" className="w-48 h-48" data-testid="einvoice-qr-image" />
              </div>
            )}
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-500">QR Payload</summary>
              <pre className="mt-2 bg-slate-50 p-2 border border-slate-200 rounded-sm overflow-auto text-[10px]">{einv.qr_payload}</pre>
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
